interface DeletionWarningModalProps {
  userRole: 'buyer' | 'seller';
  userName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isValidating: boolean;
  validationError: string;
}

export function DeletionWarningModal({
  userRole,
  userName,
  onConfirm,
  onCancel,
  isValidating,
  validationError
}: DeletionWarningModalProps) {
  const getUserRoleText = () => {
    return userRole === 'buyer' ? '구매자' : '판매자';
  };

  const getWarningMessage = () => {
    if (userRole === 'buyer') {
      return [
        '계정을 삭제하면 다음 정보가 모두 삭제됩니다:',
        '• 개인정보 및 프로필',
        '• 저장된 주소 정보',
        '• 즐겨찾기 매장',
        '• 쿠폰 및 포인트',
        '• 장바구니 내역',
        '• 개인 알림 설정'
      ];
    } else {
      return [
        '계정을 삭제하면 다음 정보가 모두 삭제됩니다:',
        '• 판매자 정보 및 프로필',
        '• 매장 정보 및 이미지',
        '• 등록된 모든 상품',
        '• 영업시간 및 서비스 설정',
        '• 재고 관리 정보'
      ];
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto">
        {/* 경고 아이콘 */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.083 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
      </div>

      {/* 제목 */}
      <div className="text-center mb-6">
        <h4 className="text-lg font-bold text-gray-900 mb-2">
          정말로 계정을 삭제하시겠습니까?
        </h4>
        <p className="text-sm text-gray-600">
          {userName}님의 {getUserRoleText()} 계정이 완전히 삭제됩니다
        </p>
      </div>

      {/* 경고 메시지 */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.083 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div className="flex-1">
            <h5 className="text-sm font-medium text-red-800 mb-2">
              ⚠️ 삭제될 정보
            </h5>
            <div className="text-sm text-red-700 space-y-1">
              {getWarningMessage().map((message, index) => (
                <p key={index} className={index === 0 ? 'font-medium mb-2' : ''}>
                  {message}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 주의사항 */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h5 className="text-sm font-medium text-yellow-800 mb-2">
              💡 중요 안내
            </h5>
            <div className="text-sm text-yellow-700 space-y-1">
              <p>• 삭제된 계정과 정보는 복구할 수 없습니다</p>
              <p>• 주문 관련 정보는 정산 및 분쟁 처리를 위해 보존됩니다</p>
              <p>• 동일한 이메일로 재가입이 가능합니다</p>
              {userRole === 'seller' && (
                <p>• 진행 중인 주문이 모두 완료된 후 삭제가 가능합니다</p>
              )}
            </div>
          </div>
        </div>
      </div>

        {/* 검증 에러 메시지 */}
        {validationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{validationError}</p>
            </div>
          </div>
        )}
      </div>

      {/* 고정된 버튼 영역 */}
      <div className="border-t border-gray-200 p-4 bg-white">{/* 버튼들 */}
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={isValidating}
          className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          취소
        </button>
        
        <button
          onClick={onConfirm}
          disabled={isValidating}
          className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isValidating ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              확인 중...
            </div>
          ) : (
            '계속 진행'
          )}
        </button>
      </div>
      </div>
    </div>
  );
}